<script setup>
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import {
  required,
  minLength,
  requiredIf,
  minValue,
} from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import { formatBytes } from 'shared/helpers/FileHelper';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import ContactMultiSelectComboBox from 'dashboard/components-next/combobox/ContactMultiSelectComboBox.vue';
import ContactAPI from 'dashboard/api/contacts';
import { API_CAMPAIGN_MESSAGE_CHARACTER_LIMIT } from 'shared/constants/campaign';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getApiInboxes'),
};

const MAX_API_ATTACHMENTS = 5;

const getInitialState = () => ({
  title: '',
  message: '',
  inboxId: null,
  scheduledAt: null,
  audienceType: 'tags', // 'tags', 'contacts'
  selectedAudience: [],
  selectedContacts: [],
  // Advanced options (optional)
  intervalMinSeconds: 20,
  intervalMaxSeconds: 45,
  showAdvanced: false,
  attachments: [],
});

const state = reactive(getInitialState());
const attachmentInputRef = ref(null);
const isUploadingAttachment = ref(false);

const rules = computed(() => {
  const baseRules = {
    title: { required, minLength: minLength(1) },
    message: {
      required: requiredIf(() => state.attachments.length === 0),
      minLength: value => !value || value.length >= 1,
    },
    inboxId: { required },
    scheduledAt: { required },
    // When advanced options are visible, validate intervals similar to Evolution
    intervalMinSeconds: {
      required: requiredIf(() => state.showAdvanced),
      minValue: minValue(0),
      lteTenMin: value => !state.showAdvanced || Number(value || 0) <= 600,
      lteMaxMinusThirty: value =>
        !state.showAdvanced ||
        Number(value || 0) <= Number(state.intervalMaxSeconds || 0) - 20,
    },
    intervalMaxSeconds: {
      required: requiredIf(() => state.showAdvanced),
      minValue: minValue(20),
      lteTenMin: value => !state.showAdvanced || Number(value || 0) <= 600,
      gteMinPlusThirty: value =>
        !state.showAdvanced ||
        Number(value || 0) >= Number(state.intervalMinSeconds || 0) + 20,
    },
  };

  if (state.audienceType === 'tags') {
    baseRules.selectedAudience = { required };
  } else if (state.audienceType === 'contacts') {
    baseRules.selectedContacts = { required };
  }

  return baseRules;
});

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({ value: item[valueKey], label: item[labelKey] })) ?? [];

const audienceTypeOptions = computed(() => [
  {
    value: 'tags',
    label: t('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE_TYPE.TAGS'),
  },
  {
    value: 'contacts',
    label: t('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE_TYPE.CONTACTS'),
  },
]);

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const attachmentTexts = computed(() => ({
  label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.LABEL'),
  upload: t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.UPLOAD'),
  uploading: t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.UPLOADING'),
  limitReached: t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.LIMIT_REACHED'),
  uploadError: t('CAMPAIGN.WHATSAPP.CREATE.FORM.ATTACHMENTS.UPLOAD_ERROR'),
  help: t('CAMPAIGN.API.CREATE.FORM.ATTACHMENTS.HELP_TEXT', {
    limit: MAX_API_ATTACHMENTS,
  }),
}));

// Audience helpers for recipients count
const selectedLabelTitles = computed(() => {
  const labelMap = new Map(
    (formState.labels.value || []).map(l => [Number(l.id), l.title])
  );
  return (state.selectedAudience || [])
    .map(id => labelMap.get(Number(id)))
    .filter(Boolean);
});
const recipientsCount = ref(0);
let lastAudienceRequestId = 0;
const fetchRecipientsCount = async () => {
  const titles = selectedLabelTitles.value;
  if (!titles || titles.length === 0) {
    recipientsCount.value = 0;
    return;
  }
  // eslint-disable-next-line no-plusplus
  const requestId = ++lastAudienceRequestId;
  try {
    const payload = titles.map((title, idx) => ({
      attribute_key: 'labels',
      filter_operator: 'equal_to',
      values: [title],
      attribute_model: 'standard',
      ...(idx !== titles.length - 1 ? { query_operator: 'or' } : {}),
    }));
    const {
      data: { meta },
    } = await ContactAPI.filter(undefined, 'name', { payload });
    if (requestId === lastAudienceRequestId) {
      recipientsCount.value = Number(meta?.count || 0);
    }
  } catch (_) {
    if (requestId === lastAudienceRequestId) {
      recipientsCount.value = 0;
    }
  }
};
watch(selectedLabelTitles, fetchRecipientsCount, { immediate: true });

const canAddMoreAttachments = computed(
  () => state.attachments.length < MAX_API_ATTACHMENTS
);

const minTotalSeconds = computed(() => {
  const count = Math.max(0, Number(recipientsCount.value || 0) - 1);
  return count * Number(state.intervalMinSeconds || 0);
});

const maxTotalSeconds = computed(() => {
  const count = Math.max(0, Number(recipientsCount.value || 0) - 1);
  return count * Number(state.intervalMaxSeconds || 0);
});

const formatDuration = secs => {
  const total = Math.max(0, Math.floor(Number(secs || 0)));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
};

const handleAttachmentButtonClick = () => {
  if (!canAddMoreAttachments.value) {
    useAlert(
      t('CAMPAIGN.API.CREATE.FORM.ATTACHMENTS.LIMIT_ALERT', {
        limit: MAX_API_ATTACHMENTS,
      })
    );
    return;
  }

  attachmentInputRef.value?.click?.();
};

const handleAttachmentChange = async event => {
  const files = Array.from(event?.target?.files || []);
  if (!files.length) return;

  let availableSlots = MAX_API_ATTACHMENTS - state.attachments.length;
  if (availableSlots <= 0) {
    useAlert(
      t('CAMPAIGN.API.CREATE.FORM.ATTACHMENTS.LIMIT_ALERT', {
        limit: MAX_API_ATTACHMENTS,
      })
    );
    if (event?.target) event.target.value = '';
    return;
  }

  const filesToUpload = files.slice(0, availableSlots);
  isUploadingAttachment.value = true;
  try {
    const uploads = await Promise.all(
      filesToUpload.map(async file => {
        const { blobId } = await uploadFile(file);
        return {
          blobId,
          name: file.name,
          size: file.size,
          type: file.type,
        };
      })
    );

    if (uploads.length) {
      state.attachments.push(...uploads);
    }

    if (files.length > filesToUpload.length) {
      useAlert(attachmentTexts.value.limitReached);
    }
  } catch (error) {
    useAlert(attachmentTexts.value.uploadError);
  } finally {
    isUploadingAttachment.value = false;
    if (event?.target) event.target.value = '';
  }
};

const removeAttachmentAt = index => {
  state.attachments.splice(index, 1);
};

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.API.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => {
  const errors = {
    title: getErrorMessage('title', 'TITLE'),
    message: getErrorMessage('message', 'MESSAGE'),
    inbox: getErrorMessage('inboxId', 'INBOX'),
    scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
    intervalMinSeconds: v$.value.intervalMinSeconds?.$error
      ? t('CAMPAIGN.API.CREATE.FORM.INTERVAL.MIN_ERROR')
      : '',
    intervalMaxSeconds: v$.value.intervalMaxSeconds?.$error
      ? t('CAMPAIGN.API.CREATE.FORM.INTERVAL.MAX_ERROR')
      : '',
  };

  if (state.audienceType === 'tags') {
    errors.audience = getErrorMessage('selectedAudience', 'AUDIENCE');
  } else if (state.audienceType === 'contacts') {
    errors.contacts = getErrorMessage('selectedContacts', 'CONTACTS');
  }

  return errors;
});

const isSubmitDisabled = computed(() => v$.value.$invalid);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, getInitialState());
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  const templateParams = {
    interval_min_seconds: Number(state.intervalMinSeconds || 0),
    interval_max_seconds: Number(state.intervalMaxSeconds || 0),
  };

  let audience = [];
  if (state.audienceType === 'tags') {
    audience = state.selectedAudience?.map(id => ({ id, type: 'Label' }));
  } else if (state.audienceType === 'contacts') {
    audience = state.selectedContacts?.map(id => ({ id, type: 'Contact' }));
  }

  const base = {
    title: state.title,
    message: state.message,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience_type: state.audienceType,
    audience,
    attachments: state.attachments.map(attachment => attachment.blobId),
  };

  const shouldIncludeTemplateParams =
    state.showAdvanced ||
    templateParams.interval_min_seconds > 0 ||
    templateParams.interval_max_seconds > 0;

  if (shouldIncludeTemplateParams) {
    base.template_params = templateParams;
  }

  return base;
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

// Threshold alerts to guide safe ranges
watch(
  () => state.intervalMinSeconds,
  (value, oldValue) => {
    if (
      state.showAdvanced &&
      Number(value || 0) >= 570 &&
      Number(oldValue || 0) < 570
    ) {
      useAlert(t('CAMPAIGN.API.CREATE.FORM.INTERVAL.MIN_THRESHOLD_ALERT'));
    }
    if (
      state.showAdvanced &&
      Number(value || 0) < 10 &&
      Number(oldValue || 0) >= 10
    ) {
      useAlert(
        t('CAMPAIGN.API.CREATE.FORM.INTERVAL.MIN_TEN_LOWER_BOUND_ALERT')
      );
    }
    if (
      state.showAdvanced &&
      Number(value || 0) < 0 &&
      Number(oldValue || 0) >= 0
    ) {
      useAlert(t('CAMPAIGN.API.CREATE.FORM.INTERVAL.MIN_LOWER_BOUND_ALERT'));
    }
  }
);
watch(
  () => state.intervalMaxSeconds,
  (value, oldValue) => {
    if (
      state.showAdvanced &&
      Number(value || 0) >= 600 &&
      Number(oldValue || 0) < 600
    ) {
      useAlert(t('CAMPAIGN.API.CREATE.FORM.INTERVAL.EXCEED_ALERT'));
    }
    if (
      state.showAdvanced &&
      Number(value || 0) < 20 &&
      Number(oldValue || 0) >= 20
    ) {
      useAlert(t('CAMPAIGN.API.CREATE.FORM.INTERVAL.MAX_LOWER_BOUND_ALERT'));
    }
  }
);

// Alert if range becomes less than 30s (max - min < 30)
watch(
  () => state.intervalMinSeconds,
  (value, oldValue) => {
    if (!state.showAdvanced) return;
    const max = Number(state.intervalMaxSeconds || 0);
    const newDiff = max - Number(value || 0);
    const oldDiff = max - Number(oldValue || 0);
    if (oldDiff >= 30 && newDiff < 30) {
      useAlert(t('CAMPAIGN.API.CREATE.FORM.INTERVAL.RANGE_ALERT'));
    }
  }
);
watch(
  () => state.intervalMaxSeconds,
  (value, oldValue) => {
    if (!state.showAdvanced) return;
    const min = Number(state.intervalMinSeconds || 0);
    const newDiff = Number(value || 0) - min;
    const oldDiff = Number(oldValue || 0) - min;
    if (oldDiff >= 30 && newDiff < 30) {
      useAlert(t('CAMPAIGN.API.CREATE.FORM.INTERVAL.RANGE_ALERT'));
    }
  }
);

// Reset audience selections when audience type changes
watch(
  () => state.audienceType,
  () => {
    state.selectedAudience = [];
    state.selectedContacts = [];
  }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.API.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.API.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.API.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.API.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <!-- Audience Type Selector -->
    <div class="flex flex-col gap-1">
      <label
        for="audienceType"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE_TYPE.LABEL') }}
      </label>
      <ComboBox
        id="audienceType"
        v-model="state.audienceType"
        :options="audienceTypeOptions"
        :placeholder="t('CAMPAIGN.SMS.CREATE.FORM.AUDIENCE_TYPE.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <!-- Tags Audience -->
    <div v-if="state.audienceType === 'tags'" class="flex flex-col gap-1">
      <div class="flex items-center justify-between mb-0.5">
        <label for="audience" class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.API.CREATE.FORM.AUDIENCE.LABEL') }}
        </label>
        <span
          v-if="selectedLabelTitles.length"
          class="text-xs font-normal text-n-slate-11"
        >
          {{
            t('CAMPAIGN.API.CREATE.FORM.AUDIENCE.RECIPIENTS_COUNT', {
              count: recipientsCount,
            })
          }}
        </span>
      </div>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :max-visible-options="3"
        inline-dropdown
        :label="t('CAMPAIGN.API.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.API.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <!-- Contacts Audience -->
    <div v-if="state.audienceType === 'contacts'" class="flex flex-col gap-1">
      <label for="contacts" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.SMS.CREATE.FORM.CONTACTS.LABEL') }}
      </label>
      <ContactMultiSelectComboBox
        v-model="state.selectedContacts"
        :has-error="!!formErrors.contacts"
        :message="formErrors.contacts"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.API.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.API.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <TextArea
      v-model="state.message"
      :label="t('CAMPAIGN.API.CREATE.FORM.MESSAGE.LABEL')"
      :placeholder="t('CAMPAIGN.API.CREATE.FORM.MESSAGE.PLACEHOLDER')"
      :max-length="API_CAMPAIGN_MESSAGE_CHARACTER_LIMIT"
      show-character-count
      :message="formErrors.message"
      :message-type="formErrors.message ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ attachmentTexts.label }}
      </label>
      <div class="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          color="slate"
          variant="ghost"
          icon="i-lucide-paperclip"
          :label="
            isUploadingAttachment
              ? attachmentTexts.uploading
              : attachmentTexts.upload
          "
          :disabled="isUploadingAttachment"
          class="!w-fit"
          @click="handleAttachmentButtonClick"
        />
        <span v-if="!canAddMoreAttachments" class="text-xs text-n-amber-11">
          {{ attachmentTexts.limitReached }}
        </span>
      </div>
      <ul v-if="state.attachments.length" class="flex flex-col gap-2">
        <li
          v-for="(attachment, index) in state.attachments"
          :key="`${attachment.blobId}-${index}`"
          class="flex items-center justify-between gap-3 rounded-md border border-n-weak bg-n-alpha-black1 px-3 py-2"
        >
          <div class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">
              {{ attachment.name }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ formatBytes(attachment.size || 0) }}
            </span>
          </div>
          <Button
            type="button"
            size="xs"
            variant="ghost"
            color="slate"
            icon="i-lucide-trash"
            @click="removeAttachmentAt(index)"
          />
        </li>
      </ul>
      <p class="text-xs text-n-slate-11">
        {{ attachmentTexts.help }}
      </p>
      <input
        ref="attachmentInputRef"
        type="file"
        multiple
        class="hidden"
        @change="handleAttachmentChange"
      />
    </div>

    <!-- Advanced options: optional throttling between contacts -->
    <div>
      <button
        class="flex pl-0 items items-center gap-1 text-sm text-n-slate-12 hover:underline"
        type="button"
        @click="state.showAdvanced = !state.showAdvanced"
      >
        <span class="i-lucide-plus size-3" />
        {{ t('CAMPAIGN.API.CREATE.FORM.ADVANCED_OPTIONS.LABEL') }}
      </button>
      <div v-if="state.showAdvanced" class="mt-2">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            v-model.number="state.intervalMinSeconds"
            type="number"
            min="0"
            max="600"
            :label="t('CAMPAIGN.API.CREATE.FORM.INTERVAL.MIN_LABEL')"
            :placeholder="
              t('CAMPAIGN.API.CREATE.FORM.INTERVAL.MIN_PLACEHOLDER')
            "
            :message="formErrors.intervalMinSeconds"
            :message-type="formErrors.intervalMinSeconds ? 'error' : 'info'"
          />
          <Input
            v-model.number="state.intervalMaxSeconds"
            type="number"
            min="0"
            max="600"
            :label="t('CAMPAIGN.API.CREATE.FORM.INTERVAL.MAX_LABEL')"
            :placeholder="
              t('CAMPAIGN.API.CREATE.FORM.INTERVAL.MAX_PLACEHOLDER')
            "
            :message="formErrors.intervalMaxSeconds"
            :message-type="formErrors.intervalMaxSeconds ? 'error' : 'info'"
          />
        </div>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('CAMPAIGN.API.CREATE.FORM.INTERVAL.INFO') }}
        </p>
      </div>
    </div>

    <!-- Summary -->
    <div
      v-if="state.showAdvanced"
      class="mt-2 p-3 rounded-md border border-n-weak bg-n-alpha-black1 text-n-slate-12"
    >
      <div class="flex items-center justify-between text-sm">
        <span>{{
          t('CAMPAIGN.API.CREATE.FORM.SEND_SUMMARY.DELAY_LABEL')
        }}</span>
        <span>
          {{
            t('CAMPAIGN.API.CREATE.FORM.SEND_SUMMARY.DELAY_RANGE', {
              min: Number(state.intervalMinSeconds || 0),
              max: Number(state.intervalMaxSeconds || 0),
            })
          }}
        </span>
      </div>
      <div
        v-if="recipientsCount > 0"
        class="flex items-center justify-between text-sm mt-1"
      >
        <span>{{
          t('CAMPAIGN.API.CREATE.FORM.SEND_SUMMARY.TOTAL_LABEL')
        }}</span>
        <span>
          {{
            t('CAMPAIGN.API.CREATE.FORM.SEND_SUMMARY.TOTAL_RANGE', {
              min: formatDuration(minTotalSeconds),
              max: formatDuration(maxTotalSeconds),
            })
          }}
        </span>
      </div>
    </div>

    <div
      class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between w-full gap-3"
    >
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.API.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.API.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
